import { useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, Link2, Heading1, Heading2,
  AlignLeft, AlignCenter, AlignRight, Code2, Eraser, Type,
} from 'lucide-react';

/**
 * Small dependency-free HTML editor (contentEditable + document.execCommand).
 *
 * Deliberately not a full WYSIWYG framework: email bodies need bold/italic/lists/links/headings and a
 * raw-HTML escape hatch, and adding a rich-text package to the bundle for that isn't worth it.
 * `Source` mode lets marketing paste a designed HTML template verbatim.
 */
export function RichTextEditor({ value, onChange, placeholder, minHeight = 260 }: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [source, setSource] = useState(false);

  // Only push external changes into the DOM when they differ, so typing isn't interrupted
  // (writing innerHTML on every keystroke would reset the caret to the start).
  useEffect(() => {
    if (source) return;
    const el = ref.current;
    if (el && el.innerHTML !== value) el.innerHTML = value || '';
  }, [value, source]);

  const exec = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML ?? '');
  };

  const addLink = () => {
    const url = window.prompt('Link URL (include https://)');
    if (!url) return;
    exec('createLink', url);
  };

  const Btn = ({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) => (
    <button type="button" onClick={onClick} title={title}
      className="p-1.5 rounded hover:bg-slate-200 text-slate-600">{children}</button>
  );

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <Btn onClick={() => exec('bold')} title="Bold"><Bold size={14} /></Btn>
        <Btn onClick={() => exec('italic')} title="Italic"><Italic size={14} /></Btn>
        <Btn onClick={() => exec('underline')} title="Underline"><Underline size={14} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => exec('formatBlock', '<h1>')} title="Heading 1"><Heading1 size={14} /></Btn>
        <Btn onClick={() => exec('formatBlock', '<h2>')} title="Heading 2"><Heading2 size={14} /></Btn>
        <Btn onClick={() => exec('formatBlock', '<p>')} title="Normal text"><Type size={14} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => exec('insertUnorderedList')} title="Bullet list"><List size={14} /></Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered size={14} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={() => exec('justifyLeft')} title="Align left"><AlignLeft size={14} /></Btn>
        <Btn onClick={() => exec('justifyCenter')} title="Align centre"><AlignCenter size={14} /></Btn>
        <Btn onClick={() => exec('justifyRight')} title="Align right"><AlignRight size={14} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn onClick={addLink} title="Insert link"><Link2 size={14} /></Btn>
        <Btn onClick={() => exec('removeFormat')} title="Clear formatting"><Eraser size={14} /></Btn>
        <button type="button" onClick={() => setSource((s) => !s)}
          title={source ? 'Back to visual editing' : 'Edit raw HTML — paste a designed template here'}
          className={`ml-auto px-2 py-1 rounded text-xs flex items-center gap-1 ${source ? 'bg-slate-800 text-white' : 'hover:bg-slate-200 text-slate-600'}`}>
          <Code2 size={13} /> {source ? 'Visual' : 'HTML'}
        </button>
      </div>

      {source ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          placeholder="<p>Paste your HTML template here…</p>"
          className="w-full p-3 font-mono text-xs outline-none resize-y"
          style={{ minHeight }} />
      ) : (
        <div ref={ref} contentEditable suppressContentEditableWarning
          onInput={() => onChange(ref.current?.innerHTML ?? '')}
          onBlur={() => onChange(ref.current?.innerHTML ?? '')}
          data-placeholder={placeholder ?? 'Write your email…'}
          className="p-3 text-sm outline-none overflow-y-auto prose-email empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
          style={{ minHeight, maxHeight: 520 }} />
      )}
    </div>
  );
}
